import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'

export interface UseReorderMutationOpts {
  /** PUT-like call. Receives the new ordered ids. */
  mutationFn: (orderedIds: number[]) => Promise<void>
  /** Query keys to invalidate on success (anything that surfaces sort_order). */
  invalidateKeys?: QueryKey[]
}

/**
 * Standard hook for "reorder a list" mutations.
 *
 * Pair with `<SortableList>` from components/common/SortableList:
 * ```ts
 * const reorder = useReorderMutation({
 *   mutationFn: ids => api.put(`/api/foo/${parentId}/bar/order`, { ids }),
 *   invalidateKeys: [['foo', parentId]],
 * })
 * <SortableList items={...} onReorder={ids => reorder.mutate(ids.map(Number))} ... />
 * ```
 */
export function useReorderMutation({ mutationFn, invalidateKeys }: UseReorderMutationOpts) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      invalidateKeys?.forEach(key => qc.invalidateQueries({ queryKey: key }))
    },
  })
}
